import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLoading } from "../../../context/LoadingContext";
import { getSocietes } from "../../../features/familles/api/societeAPI";
import { Building2, AlertCircle, RotateCcw } from "lucide-react";
import PaginationFooter from "../../../components/ui/PaginationFooter";
import { getPaginationState } from "../../../utils/pagination";
import SkeletonCard from "../../../components/ui/SkeletonCard";
import { Button } from "../../../components/ui/button";
import SocietesHeader from "../../../features/familles/components/SocietesHeader";
import SocieteCard from "../../../features/familles/components/SocieteCard";
import { filterSocietes } from "../../../features/familles/utils/societeFilters";

const PAGE_SIZE = 6;

export default function Societes() {
  const navigate = useNavigate();
  const { isLoading: isPageLoading } = useLoading();
  const [societes, setSocietes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedForm, setSelectedForm] = useState("TOUS");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchSocietes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSocietes();
      setSocietes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur de chargement des sociétés", err);
      setError("Impossible de charger la liste des sociétés. Veuillez vérifier votre connexion.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSocietes();
  }, [fetchSocietes]);

  // Filtrage combiné (normalisation textuelle + filtre juridique)
  const filteredSocietes = useMemo(() => {
    return filterSocietes(societes, searchTerm, selectedForm);
  }, [societes, searchTerm, selectedForm]);

  // Réinitialiser la page courante dès que le terme de recherche ou le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedForm]);

  const { safePage, startIndex, endIndex } = getPaginationState({
    page: currentPage,
    pageSize: PAGE_SIZE,
    totalItems: filteredSocietes.length,
  });
  const currentSocietes = filteredSocietes.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage((page) => getPaginationState({
      page,
      pageSize: PAGE_SIZE,
      totalItems: filteredSocietes.length,
    }).safePage);
  }, [filteredSocietes.length]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setSelectedForm("TOUS");
    setCurrentPage(1);
  };

  // Le loader global garde la main sur la transition de route.
  // Les blocs se montent ensuite ; la requete continue en parallele.
  if (isPageLoading) return null;

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-workspace p-4 sm:p-8">
      {/* En-tête standardisé Hipparchus avec commandes à droite */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={loading ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <SocietesHeader
          search={searchTerm}
          onSearchChange={setSearchTerm}
          selectedForm={selectedForm}
          onFormChange={setSelectedForm}
        />
      </motion.div>

      {/* Box Mère unifiée (Data Grid Container) avec animation fluide */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="flex-1 min-h-0 workspace-card overflow-hidden flex flex-col shadow-hp-sm"
      >
        {/* Zone de contenu principale */}
        <div className="flex-1 overflow-y-auto lg:overflow-hidden p-4 relative">
          {loading ? (
            <SkeletonCard count={6} />
          ) : error ? (
            <div className="flex-1 h-full flex flex-col items-center justify-center text-danger-500 py-20">
              <AlertCircle className="w-10 h-10 mb-2" />
              <p className="text-primary font-bold text-base mb-4">{error}</p>
              <Button variant="outline" onClick={fetchSocietes} className="gap-2 text-xs">
                <RotateCcw className="w-4 h-4" />
                Réessayer
              </Button>
            </div>
          ) : filteredSocietes.length === 0 ? (
            <div className="flex-1 h-full flex flex-col items-center justify-center text-subtle py-20">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-workspace" />
              {societes.length === 0 ? (
                <p className="text-primary font-medium text-sm">Aucune société n'est actuellement enregistrée.</p>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <p className="text-primary font-medium text-sm">Aucune société ne correspond à ces critères.</p>
                  <Button variant="outline" size="sm" onClick={handleResetFilters} className="text-xs mt-2">
                    Réinitialiser les critères
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-slide-up h-full lg:grid-rows-2">
              {currentSocietes.map((soc) => (
                <SocieteCard
                  key={soc.id || soc._id}
                  societe={soc}
                  onClick={() => navigate(`/prospects/societes/${soc.id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {!loading && !error && filteredSocietes.length > 0 && (
          <PaginationFooter
            page={safePage}
            pageSize={PAGE_SIZE}
            totalItems={filteredSocietes.length}
            onPageChange={setCurrentPage}
            ariaLabel="Pagination des sociétés"
          />
        )}
      </motion.div>
    </div>
  );
}
